import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { cloneDeep } from 'lodash';
import { EnumHelper, ModelTemplate, SmartSearchModel, SmartSearchState, naturalSort } from 'mbs-ui-kit';
import { SmartSearchValidationState } from 'mbs-ui-kit/smart-search/models';
import { getSmartSearchTagValues } from 'mbs-ui-kit/smart-search/models/smart-search-model';
import { AdministratorsFacade } from 'mbs-ui-shared@facades/administrators.facade';
import { BrandsFacade } from 'mbs-ui-shared@facades/brands.facade';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { CustomDnsBindingFacade } from 'mbs-ui-shared@facades/customDnsBinding.facade';
import { AdministratorInCamelCase, Company, LicensesSettings, LimitsMode, StorageType } from 'mbs-ui-shared@models';
import Brand, { BrandingCompany } from 'mbs-ui-shared@models/Brand';
import Computer from 'mbs-ui-shared@models/Computer';
import CustomDnsBinding from 'mbs-ui-shared@models/CustomDnsBinding';
import {
  DisabledSmartSearchKey,
  EnabledSmartSearchKey,
  SmartSearchTemplateKeys,
  SmartSearchTemplateMap
} from 'mbs-ui-shared@models/SmartSearchTemplates';
import { StorageAccountLegacy } from 'mbs-ui-shared@models/StorageAccount';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { Observable, asapScheduler, of, scheduled } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { PagingAndDataFacade } from '../store/withPagingAndDataStoreFeature/pagingAndData.facade';
import SmartSearchTemplatesBase from './smart-search-templates-base';

@Injectable({ providedIn: 'root' })
export class SmartSearchTemplates extends SmartSearchTemplatesBase {
  private injector = inject(Injector);
  private storageAccountsService = inject(StorageAccountsService);
  private administratorsFacade = inject(AdministratorsFacade);
  private computersFacade = inject(ComputersFacade);
  private companiesFacade = inject(CompaniesFacade);
  private brandsFacade = inject(BrandsFacade);
  private bindingsFacade = inject(CustomDnsBindingFacade);

  protected readonly maxTake = 10;
  protected get maxTakeStr(): string {
    return String(this.maxTake);
  }

  protected getSimpleModelItems<T>(
    tag: string,
    cachePrefix: string,
    request: (search: string, take: string) => Observable<T[]>,
    formatter: (item: T) => string,
    addGroupBrackets = true,
    filterDataByTerm: (data: T[], term: string) => T[] = undefined
  ): ModelTemplate<T> {
    return {
      tag,
      items: (state: SmartSearchState) =>
        this.searchTemplate$(state).pipe(
          map((term) => (filterDataByTerm ? '' : term)),
          switchMap((term) =>
            (this.cache[cachePrefix + term]
              ? of(this.cache[cachePrefix + term])
              : request(term, this.maxTakeStr).pipe(tap(this.writeToCache(cachePrefix + term)))
            ).pipe(map((data: T[]) => (filterDataByTerm ? filterDataByTerm(data, term) : data)))
          )
        ),
      itemFormatter: (value: T) => formatter(value),
      addGroupBrackets
    };
  }

  public readonly adminItems: ModelTemplate<AdministratorInCamelCase> = this.getSimpleModelItems<AdministratorInCamelCase>(
    SmartSearchTemplateKeys.Admin,
    'admin ',
    (search, take) =>
      this.administratorsFacade.getAdministrators({ search, limit: parseInt(take, 10) }).pipe(map((response) => response.data)),
    (value) => value.email,
    false
  );

  public readonly computerItems: ModelTemplate<Computer> = this.getSimpleModelItems<Computer>(
    SmartSearchTemplateKeys.Computer,
    'computer ',
    (search, take) => this.computersFacade.getComputers({ search, limit: parseInt(take, 10) }).pipe(map((response) => response.data)),
    (value) => value.displayName || value.name
  );

  public readonly companiesItems: ModelTemplate<Company> = this.getSimpleModelItems<Company>(
    SmartSearchTemplateKeys.Company,
    'company ',
    (search, take) => this.companiesFacade.getCompanies({ search, limit: parseInt(take, 10) }).pipe(map((response) => response.data)),
    (value) => value.name.toString()
  );

  public readonly brandingCompaniesItems: ModelTemplate<BrandingCompany> = this.getSimpleModelItems<BrandingCompany>(
    SmartSearchTemplateKeys.Company,
    'brandingCompany_',
    () => this.brandsFacade.getAllBrandingCompanies$(),
    (value) => value.name.toString(),
    true,
    (data, term) => {
      const filtered = this.filterBrandingCompanyByTerm(data, term);
      return filtered.length > this.maxTake ? filtered.slice(0, this.maxTake) : filtered;
    }
  );

  public readonly adminStatusItems: ModelTemplate<string> = {
    tag: SmartSearchTemplateKeys.Status,
    items: (state: SmartSearchState) =>
      this.searchTemplate$(state).pipe(
        map((term) =>
          term
            ? term === EnabledSmartSearchKey || term === DisabledSmartSearchKey
              ? [term]
              : []
            : [EnabledSmartSearchKey, DisabledSmartSearchKey]
        )
      ),
    itemFormatter: (value: string) => value
  };

  public readonly NotificationErrorsType: ModelTemplate<string> = {
    tag: SmartSearchTemplateKeys.Type
  };

  public readonly licensesSettingsItems = (isPostPayment: boolean): ModelTemplate<string> => {
    return {
      tag: SmartSearchTemplateKeys.LicensesSettings,
      items: (state: SmartSearchState) =>
        this.searchTemplate$(state).pipe(
          map((term) => {
            const lowerTerm = term.toLocaleLowerCase();
            return EnumHelper.EnumToArray(isPostPayment ? LimitsMode : LicensesSettings)
              .filter((l) => l.toLocaleLowerCase().includes(lowerTerm))
              .slice(0, this.maxTake);
          })
        ),
      itemFormatter: (value: string) => value
    };
  };

  public readonly storageAccountTypeItems: ModelTemplate<string> = {
    tag: SmartSearchTemplateKeys.StorageType,
    items: (state: SmartSearchState) =>
      this.searchTemplate$(state).pipe(
        switchMap((term) => {
          return this.storageAccountsService.loaded$.pipe(
            switchMap((state) => (state ? this.storageAccountsService.entities$ : this.storageAccountsService.getAll())),
            first(),
            map((data) => this.filterStorageTypeByTerm(data, term))
          );
        })
      ),
    itemFormatter: (value: string) => value
  };

  public readonly brandsItems: ModelTemplate<Brand> = this.getSimpleModelItems<Brand>(
    SmartSearchTemplateKeys.BrandingName,
    'brand_',
    () => this.brandsFacade.getAll$(),
    (value) => value.brandName.toString(),
    true,
    (data, term) => this.filterBrandsByTerm(data, term)
  );

  private customBindingsDnsNameTemplate = SmartSearchTemplateMap.get(SmartSearchTemplateKeys.DNSName);
  public readonly CustomBindingsDnsNameItems: ModelTemplate<CustomDnsBinding> = {
    tag: this.customBindingsDnsNameTemplate.tag,
    filterByProp: this.customBindingsDnsNameTemplate.prop,
    items: (state): Observable<CustomDnsBinding[]> =>
      this.getStringItems(this.bindingsFacade, state, this.customBindingsDnsNameTemplate.prop),
    itemFormatter: (model): string => model[this.customBindingsDnsNameTemplate.prop],
    addGroupBrackets: false,
    isSequenceMultiple: false
  };

  private filterBrandsByTerm(brands: Brand[], term: string): Brand[] {
    return term ? brands.filter((brand) => brand.brandName.toLowerCase().includes(term.toLowerCase())) : brands;
  }

  private filterBrandingCompanyByTerm(brandingCompany: BrandingCompany[], term: string): BrandingCompany[] {
    return term ? brandingCompany.filter((company) => company.name.toLowerCase().includes(term.toLowerCase())) : brandingCompany;
  }
  private filterStorageTypeByTerm(storageAccounts: StorageAccountLegacy[], term: string): string[] {
    const storageTypeEnum = StorageType;
    let filtered: StorageAccountLegacy[];

    if (term) {
      const termLowerCase = term.toLowerCase();
      filtered = storageAccounts.filter((storageAccount) => {
        return storageTypeEnum[storageAccount.StorageType]
          ? storageTypeEnum[storageAccount.StorageType].toLowerCase().includes(termLowerCase)
          : false;
      });
    } else {
      filtered = storageAccounts.filter((storageAccount) => !!storageTypeEnum[storageAccount.StorageType]);
    }
    const result = filtered
      .sort((a: StorageAccountLegacy, b: StorageAccountLegacy) =>
        storageTypeEnum[a.StorageType].toLowerCase() > storageTypeEnum[b.StorageType].toLowerCase() ? 1 : -1
      )
      .map((x) => storageTypeEnum[x.StorageType]);

    return [...new Set(result)];
  }

  private getStringItems<T extends object, F extends object = Record<string, never>>(
    facade: PagingAndDataFacade<T, F>,
    state: SmartSearchState,
    prop: string
  ): Observable<T[]> {
    const term = state.leftCaretValue;
    facade.loadData();
    return scheduled(
      runInInjectionContext(this.injector, () => toObservable(facade.data)).pipe(
        map((statistics) => cloneDeep(statistics)),
        map((statistics) => {
          const filtered = statistics.filter((stat) => stat[prop].toLowerCase().includes(term));
          return this.getUniqueListByProp(filtered, prop)
            .sort((a, b) => naturalSort(a[prop].toLowerCase(), b[prop].toLowerCase()))
            .slice(0, 10);
        })
      ),
      asapScheduler
    );
  }

  private getUniqueListByProp<T>(items: T[], prop: string): T[] {
    const getProp = (obj, prop) => {
      return obj[prop];
    };
    return items.reduce((unique: T[], item: T): T[] => {
      return unique.some((u) => getProp(u, prop) === getProp(item, prop)) ? unique : [...unique, item];
    }, [] as T[]);
  }

  public isSmartFilterValid(
    search: SmartSearchModel,
    tag: SmartSearchTemplateKeys,
    data$: Observable<string[]>
  ): Observable<SmartSearchValidationState> {
    return of(getSmartSearchTagValues(SmartSearchTemplateMap.get(tag).tag, search)).pipe(
      switchMap((searchTags) =>
        searchTags.length
          ? data$.pipe(
              map((allItems) => allItems.map((item) => item.toLowerCase())),
              map((allItems) => searchTags.every((tagName) => allItems.includes(tagName.toLowerCase()))),
              map((result) => (result ? SmartSearchValidationState.Valid : SmartSearchValidationState.Invalid))
            )
          : of(SmartSearchValidationState.Unknown)
      )
    );
  }
}
